import React, { useState, useEffect } from "react";
import AdminSidebar from "../../component/AdminComp/AdminSidebar";
import AdminTopbar from "../../component/AdminComp/AdminTopbar";
import { Download, MessageSquare, User, Calendar, Eye, Trash2 } from "react-feather";
import { Modal, Button } from "react-bootstrap";
import * as XLSX from "xlsx";
import axios from "axios";
import { baseurl } from "../../Baseurl";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AdminContact = () => {
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getAllInquiries`);
      if (response.data.success) {
        setInquiries(response.data.data);
        setFilteredInquiries(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      Swal.fire("Error", "Failed to load inquiries", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  // Filtering Logic
  useEffect(() => {
    let filtered = [...inquiries];
    if (search) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter(
        (iq) =>
          iq.first_name?.toLowerCase().includes(lowerSearch) ||
          iq.last_name?.toLowerCase().includes(lowerSearch) ||
          iq.email?.toLowerCase().includes(lowerSearch) ||
          iq.phone?.includes(search)
      );
    }
    if (statusFilter !== "All") {
      filtered = filtered.filter((iq) => iq.status === statusFilter);
    }
    if (startDate && endDate) {
      filtered = filtered.filter((iq) => {
        const iqDate = new Date(iq.created_at);
        return iqDate >= startDate && iqDate <= endDate;
      });
    }
    setFilteredInquiries(filtered);
  }, [search, statusFilter, startDate, endDate, inquiries]);

  // Update Status Logic
  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const response = await axios.put(`${baseurl}updateInquiryStatus/${id}`, {
        status: newStatus,
      });
      if (response.data.success) {
        Swal.fire("Updated!", `Status marked as ${newStatus}`, "success");
        fetchInquiries();
        if (selectedInquiry && selectedInquiry.id === id) {
          setSelectedInquiry({ ...selectedInquiry, status: newStatus });
        }
      }
    } catch (error) {
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  // Delete Logic
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This inquiry will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(`${baseurl}deleteContactForm/${id}`);
          if (response.data.success) {
            Swal.fire("Deleted!", "Inquiry has been removed.", "success");
            setShowModal(false); // Close modal if open
            fetchInquiries();
          }
        } catch (error) {
          Swal.fire("Error", "Failed to delete inquiry", "error");
        }
      }
    });
  };

  const handleViewDetails = (iq) => {
    setSelectedInquiry(iq);
    setShowModal(true);
  };

  const handleDownloadExcel = () => {
    const dataToExport = filteredInquiries.map((iq) => ({
      "First Name": iq.first_name,
      "Last Name": iq.last_name,
      Email: iq.email,
      Phone: iq.phone,
      Message: iq.message,
      Status: iq.status,
      Date: new Date(iq.created_at).toLocaleString(),
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contact Inquiries");
    XLSX.writeFile(wb, "contact_inquiries.xlsx");
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Pending": return "badge bg-warning text-dark";
      case "In-Progress": return "badge bg-info text-white";
      case "Resolved": return "badge bg-success text-white";
      default: return "badge bg-secondary";
    }
  };

  return (
    <div className="App d-flex">
      <AdminSidebar />
      <div className="content flex-grow-1 p-3">
        <AdminTopbar />
        <div className="container-fluid">
          <h4 className="text-start mb-3">Customer Inquiries</h4>
          
          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <input type="text" className="form-control" placeholder="Search..." onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="col-md-2">
              <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} placeholderText="Start Date" className="form-control" />
            </div>
            <div className="col-md-2">
              <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} placeholderText="End Date" className="form-control" />
            </div>
            <div className="col-md-2">
              <select className="form-control" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="All">All Status</option>
                <option value="Pending">Pending</option>
                <option value="In-Progress">In-Progress</option>
                <option value="Resolved">Resolved</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-success w-100" onClick={handleDownloadExcel}>
                <Download size={17} /> Export
              </button>
            </div>
          </div>

          <div className="table-responsive bg-white shadow-sm p-3 rounded">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2">Loading...</p>
              </div>
            ) : (
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Customer Details</th>
                    <th>Message Snippet</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInquiries.map((iq, index) => (
                    <tr key={iq.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="fw-bold">{iq.first_name} {iq.last_name}</span>
                          <small className="text-muted">{iq.email}</small>
                        </div>
                      </td>
                      <td style={{ maxWidth: "200px" }}>
                        <div className="text-truncate small text-muted">{iq.message}</div>
                      </td>
                      <td><small>{new Date(iq.created_at).toLocaleDateString("en-GB")}</small></td>
                      <td><span className={getStatusBadgeClass(iq.status)}>{iq.status}</span></td>
                      <td>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-outline-primary" title="View" onClick={() => handleViewDetails(iq)}>
                            <Eye size={14} />
                          </button>
                          <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => handleDelete(iq.id)}>
                            <Trash2 size={14} />
                          </button>
                          <select 
                            className="form-select form-select-sm w-auto"
                            value={iq.status}
                            onChange={(e) => handleStatusUpdate(iq.id, e.target.value)}
                          >
                            <option value="Pending">Pending</option>
                            <option value="In-Progress">In-Progress</option>
                            <option value="Resolved">Resolved</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>Inquiry Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInquiry && (
            <div className="row">
              <div className="col-md-5 border-end">
                <h6 className="text-primary mb-3"><User size={18} className="me-2"/>Customer Info</h6>
                <p className="mb-1"><strong>Name:</strong> {selectedInquiry.first_name} {selectedInquiry.last_name}</p>
                <p className="mb-1"><strong>Email:</strong> <a href={`mailto:${selectedInquiry.email}`}>{selectedInquiry.email}</a></p>
                <p className="mb-1"><strong>Phone:</strong> {selectedInquiry.phone}</p>
                <p className="mb-1"><strong>Date:</strong> {new Date(selectedInquiry.created_at).toLocaleString()}</p>
                <hr />
                <h6 className="text-primary mb-3">Management</h6>
                <label className="small fw-bold">Update Status:</label>
                <select 
                    className="form-select mb-3"
                    value={selectedInquiry.status}
                    onChange={(e) => handleStatusUpdate(selectedInquiry.id, e.target.value)}
                >
                    <option value="Pending">Pending</option>
                    <option value="In-Progress">In-Progress</option>
                    <option value="Resolved">Resolved</option>
                </select>
                <Button variant="outline-danger" className="w-100 btn-sm" onClick={() => handleDelete(selectedInquiry.id)}>
                   <Trash2 size={14} className="me-2" /> Delete Inquiry
                </Button>
              </div>
              <div className="col-md-7">
                <h6 className="text-primary mb-3"><MessageSquare size={18} className="me-2"/>Message Body</h6>
                <div className="p-3 bg-light rounded border shadow-sm" style={{ minHeight: "200px", whiteSpace: "pre-wrap" }}>
                  {selectedInquiry.message}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminContact;